import { useMemo, useState } from 'react'
import { UserCog, UserMinus, AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../lib/shadcn/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../lib/shadcn/select'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useBenchDriverWithCause, useMoveAllocationSeat } from '../../hooks/backend/fleet'
import { type AllocationSeat, type VehicleOption, vehicleLabel } from '../../utils/allocation'

const KEEP_SEAT = 'keep'
// "No vehicle" is an option in the same dropdown as the vehicles, because taking
// a man off a car and putting him on another one are the same decision made two
// ways, and a manager should not have to find a second control for the second
// one.
const BENCH = 'bench'

// Whose reason decides his pay (db/migrations/8800, clause 4.14 to 4.14B, 11A.4):
// ours pays the base and then the lay-off allowance, his own is not paid. So the
// bench asks for it here, in a manager's words, and the database refuses "his
// own" without the reason written down.
const CAUSES: { value: string; label: string }[] = [
  { value: 'fleetly', label: 'Ours: no car, no partner, car off the road' },
  { value: 'driver', label: 'His own reason, not paid' },
  { value: 'suspension', label: 'Suspended' },
  { value: 'leave', label: 'On leave' },
  { value: 'leaving', label: 'Resigning or leaving' },
]

// Moves one driver to another vehicle, optionally to a specific seat number.
// Leaving the seat number as "keep the same seat" sends null for
// new_shift_index, which tells the function to keep the current seat.
//
// Choosing "Bench, no vehicle" instead clears the seat and records a bench with
// the reason, the author and the date. The reason field is shared by both paths
// and is required by both, in the UI and again in the database.
export function ManualChangeSeatDialog({
  seat,
  vehicles,
  onMoved,
}: {
  // A filled seat: it has a seat_id and a driver to move.
  seat: AllocationSeat
  vehicles: VehicleOption[]
  onMoved: () => void
}) {
  const [open, setOpen] = useState(false)
  const [targetVehicleId, setTargetVehicleId] = useState<string>('')
  const [newSeat, setNewSeat] = useState<string>(KEEP_SEAT)
  const [reason, setReason] = useState('')
  const [cause, setCause] = useState('')
  const move = useMoveAllocationSeat()
  const bench = useBenchDriverWithCause()

  const benching = targetVehicleId === BENCH
  const busy = move.loading || bench.loading
  const reasonValid = reason.trim().length > 0
  const canSubmit = targetVehicleId !== '' && reasonValid && (!benching || cause !== '') && seat.seat_id != null && !busy

  const sortedVehicles = useMemo(
    () => [...vehicles].sort((a, b) => a.plate.localeCompare(b.plate)),
    [vehicles],
  )

  function reset() {
    setTargetVehicleId('')
    setNewSeat(KEEP_SEAT)
    setReason('')
    setCause('')
  }

  async function handleBench() {
    if (!canSubmit || !seat.seat_id) return
    try {
      await bench.trigger({ seat_id: seat.seat_id, reason: reason.trim(), cause, cause_note: reason.trim() }).result
      toast.success('Driver benched', {
        description: `${seat.driver_name || 'The driver'} is off ${seat.plate} and on the bench.`,
      })
      setOpen(false)
      reset()
      onMoved()
    } catch (err) {
      // Surface the message the DB function raised, verbatim.
      toast.error('Not benched', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  async function handleMove() {
    if (!canSubmit || !seat.seat_id) return
    if (benching) return handleBench()
    try {
      await move.trigger({
        seat_id: seat.seat_id,
        new_vehicle_id: targetVehicleId,
        new_shift_index: newSeat === KEEP_SEAT ? null : Number(newSeat),
        reason: reason.trim(),
      }).result
      toast.success('Driver moved', {
        description: `${seat.driver_name || 'The driver'} has been moved.`,
      })
      setOpen(false)
      reset()
      onMoved()
    } catch (err) {
      // Surface the message the DB function raised, verbatim.
      toast.error('Move not done', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) reset()
        setOpen(next)
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-6 gap-1 px-1.5 text-[11px]">
          <UserCog className="h-3 w-3" />
          Manual change
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{benching ? 'Bench driver' : 'Move driver'}</DialogTitle>
          <DialogDescription>
            Move {seat.driver_name || 'this driver'} off {seat.plate}, {seat.seat_label}, to another
            vehicle or seat, or bench them with a reason.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Moving
            </p>
            <p className="mt-0.5 font-medium text-foreground">
              {seat.driver_name || 'Unnamed driver'}
            </p>
            <p className="text-xs text-muted-foreground">
              From {seat.plate}, {seat.seat_label}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="move-vehicle">Move to vehicle</Label>
            <Select value={targetVehicleId} onValueChange={setTargetVehicleId}>
              <SelectTrigger id="move-vehicle">
                <SelectValue placeholder="Choose a vehicle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={BENCH}>Bench, no vehicle</SelectItem>
                {sortedVehicles.map((v) => (
                  <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                    {vehicleLabel(v)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {benching ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs leading-snug text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
              A benched driver keeps his job and stays eligible to drive, but he holds no seat, so he
              works no shifts and earns no shift pay while the bench stands. Release him from the
              bench when he is due back.
              <div className="mt-2 space-y-1.5">
                <Label htmlFor="bench-cause" className="text-amber-900 dark:text-amber-200">Whose reason is it?</Label>
                <Select value={cause} onValueChange={setCause}>
                  <SelectTrigger id="bench-cause" className="bg-background">
                    <SelectValue placeholder="Choose whose reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {CAUSES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p>Ours pays his base for the first 30 days, then the lay-off allowance. His own is not paid.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="move-seat">Seat</Label>
              <Select value={newSeat} onValueChange={setNewSeat}>
                <SelectTrigger id="move-seat">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={KEEP_SEAT}>Keep the same seat number</SelectItem>
                  <SelectItem value="1">Seat 1</SelectItem>
                  <SelectItem value="2">Seat 2</SelectItem>
                  <SelectItem value="3">Seat 3</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="move-reason">Reason</Label>
            <Textarea
              id="move-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                benching
                  ? 'Why is this driver being benched? He must be able to be told.'
                  : 'Why is this driver changing vehicles? A driver must be able to be told.'
              }
              rows={3}
            />
            {!reasonValid && reason.length > 0 && (
              <p className="text-xs text-destructive">A reason cannot be blank.</p>
            )}
          </div>

          {(move.error || bench.error) && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{move.error || bench.error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleMove} disabled={!canSubmit} className="gap-1.5">
            {busy ? (
              <Spinner className="h-4 w-4" />
            ) : benching ? (
              <UserMinus className="h-4 w-4" />
            ) : (
              <UserCog className="h-4 w-4" />
            )}
            {benching ? 'Bench driver' : 'Move driver'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
